import * as React from 'react';
import { ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// material-ui
import {
  Badge,
  Box,
  Button,
  Checkbox,
  InputAdornment,
  OutlinedInput,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

// project imports
import { i18nValue } from 'utils';
import ContextActionGuard from 'utils/guards/ContextActionGuard';
import MainCard from 'components/cards/MainCard';
import Chip from 'components/assets/Chip';
import useConfig from 'hooks/useConfig';

// assets
import { IconSearch, IconX } from '@tabler/icons';

// types
import {
  ArrangementOrder,
  EnhancedTableHeadProps,
  HeadCell,
  PageType
} from 'models';
import { FilterCategory, Row, RowCell, TableAction } from 'models/table';
import {
  AppModule,
  rowsPerPageOptions,
  TableActionType,
  TableRowCellType
} from 'utils/constants';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Filter from 'components/assets/Filter';
import { FilterStateType } from 'components/assets/FilterContent';
import { Theme } from '@mui/material/styles';
import TableClickableCell from './TableClickableCell';
import { StyledActionButton } from 'views/modules/Dispatcher/dispatcherUtils/dispatcherUtils';
import NotFoundRow from 'components/tables/NotFoundRow';

interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
  page: number;
}

interface BasicTableProps {
  header: HeadCell[];
  rows: Row[];
  title?: string;
  actions?: TableAction[];
  pagination?: PageType;
  batchProcessing?: boolean;
  page?: number;
  rowsPerPage?: number;
  search?: string;
  touchedFiltersCount?: number;
  filter?: FilterCategory[];
  filterState?: FilterStateType;
  filterChange?: (values: FilterStateType | undefined) => void;
  onSortRequest?: (e: React.SyntheticEvent, p: string) => void;
  onSearchChange?: (value: string) => void;
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => void;
  orderBy?: string;
  order?: ArrangementOrder;
  scrollable?: boolean;
  i18nComponentRoot: string;
  appModule: AppModule;
  isLoading?: boolean;
}

export type SortType = {
  attribute: string;
  order: NonNullable<ArrangementOrder>;
};

export const getSortParam = (sort: SortType) => {
  return `${sort.attribute},${sort.order}`;
};

// ==============================|| TABLE - HEADER ||============================== //

export const EnhancedTableHead = ({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  headCells,
  batchProcessing
}: EnhancedTableHeadProps) => {
  const createSortHandler =
    (property: string) => (event: React.SyntheticEvent) => {
      onRequestSort(event, property);
    };

  const HeadCells = ({
    id,
    align,
    label,
    sortable,
    disablePadding
  }: HeadCell) => {
    return (
      <TableCell
        key={id}
        align={align}
        padding={disablePadding ? 'none' : 'normal'}
        sortDirection={orderBy === id ? order : undefined}
      >
        {sortable ? (
          <TableSortLabel
            active={orderBy === id}
            direction={orderBy === id ? order : 'asc'}
            onClick={createSortHandler(id)}
          >
            {label}
            {orderBy === id ? (
              <Box component='span' sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            ) : null}
          </TableSortLabel>
        ) : (
          label
        )}
      </TableCell>
    );
  };

  return (
    <TableHead>
      <TableRow>
        {batchProcessing && (
          <TableCell padding='checkbox' sx={{ pl: 3 }}>
            <Checkbox
              color='primary'
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': i18nValue('common.table.selectAll', 'Select all')
              }}
            />
          </TableCell>
        )}

        {headCells.map((headCell) => {
          const showHeadCell = headCell.show === undefined || headCell.show;
          return showHeadCell && HeadCells(headCell);
        })}
      </TableRow>
    </TableHead>
  );
};

export default function BasicTable({
  header,
  actions,
  rows,
  title,
  pagination,
  batchProcessing,
  page,
  rowsPerPage,
  touchedFiltersCount,
  filter,
  filterState,
  filterChange,
  search,
  onSortRequest,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  orderBy,
  order,
  scrollable,
  i18nComponentRoot,
  appModule,
  isLoading
}: Readonly<BasicTableProps>) {
  const { t } = useTranslation();
  const { localStorageRowsPerPage, onChangeLocaleStorageRowsPerPage } =
    useConfig();
  const [selected, setSelected] = React.useState<string[]>([]);

  const onChangePage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => {
    if (event && onRowsPerPageChange) {
      const newSize = parseInt(event.target.value, 10);
      onChangeLocaleStorageRowsPerPage(newSize);
      onRowsPerPageChange(event);
    }
  };

  const labelDisplayedRows = (paginationInfo: LabelDisplayedRowsArgs) => {
    return `${paginationInfo.from}–${paginationInfo.to} ${t(
      'common.table.of'
    )} ${
      paginationInfo.count !== -1
        ? paginationInfo.count
        : `${t('common.table.moreThan')} ${paginationInfo.to}`
    }`;
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (selected.length > 0) {
        setSelected([]);
      } else {
        const newSelectedId: string[] = rows.map((n) => n.id?.toString());
        setSelected(newSelectedId);
      }
      return;
    }
    setSelected([]);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const getEmptyRowsCount = (
    currentPage: number,
    currentRowsPerPage: number
  ) => {
    return currentPage > 0
      ? Math.max(
          0,
          (1 + currentPage) * currentRowsPerPage -
            (pagination?.totalElements ?? 0)
        )
      : 0;
  };

  const handleSortRequest = (e: React.SyntheticEvent, p: string): void => {
    if (onSortRequest) {
      onSortRequest(e, p);
    }
  };

  type RowCellElementPropsType = {
    rowCell: RowCell;
    isItemSelected: boolean;
    labelId: string;
    detailAction?: TableAction;
  };

  const RowCellElement = ({
    rowCell,
    isItemSelected,
    labelId,
    detailAction
  }: RowCellElementPropsType) => {
    if (rowCell.tooltip) {
      return (
        <Tooltip
          key={rowCell.name}
          title={rowCell.tooltip}
          placement={'bottom-end'}
          arrow
        >
          {getTableCellComponent(rowCell, rowCell.value, detailAction)}
        </Tooltip>
      );
    }

    const cellChildren = rowCell.value ? (
      <Chip label={rowCell.value} chipColor={rowCell.color} />
    ) : (
      <></>
    );

    switch (rowCell.type) {
      case TableRowCellType.CHECKBOX:
        return (
          <TableCell sx={{ pl: 3 }} padding='checkbox' key={rowCell.name}>
            <Checkbox
              color='primary'
              checked={isItemSelected}
              inputProps={{
                'aria-labelledby': labelId
              }}
            />
          </TableCell>
        );
      case TableRowCellType.ACTIONS:
        return (
          <TableCell align={rowCell.align} key={rowCell.name}>
            <Stack
              direction='row'
              alignItems='flex-end'
              justifyContent={'flex-end'}
              spacing={1}
            >
              {rowCell.value.map((action: TableAction, index: number) => {
                if (action.contextAction?.actionCode) {
                  return (
                    <ContextActionGuard
                      key={index}
                      module={appModule}
                      actionCode={action.contextAction?.actionCode}
                      resourceEntity={action.contextAction?.resourceEntity}
                      entityIdentificator={
                        action.contextAction?.entityIdentificator
                      }
                    >
                      <StyledActionButton
                        key={index}
                        onClick={action.onClick}
                        title={action.title}
                        variant={action.variant ?? 'outlined'}
                        color={action.color}
                      >
                        {action.icon}
                      </StyledActionButton>
                    </ContextActionGuard>
                  );
                }
                return (
                  <StyledActionButton
                    key={index}
                    onClick={action.onClick}
                    title={action.title}
                    variant={action.variant ?? 'outlined'}
                    color={action.color}
                  >
                    {action.icon}
                  </StyledActionButton>
                );
              })}
            </Stack>
          </TableCell>
        );
      case TableRowCellType.CHIP:
        return getTableCellComponent(rowCell, cellChildren, detailAction);
      default:
        return getTableCellComponent(rowCell, rowCell.value, detailAction);
    }
  };

  const emptyRows =
    page && rowsPerPage ? getEmptyRowsCount(page, rowsPerPage) : 0;

  const getTableCellComponent = (
    rowCell: RowCell,
    children: React.ReactNode,
    detailAction?: TableAction
  ): ReactElement => {
    const rowProps: SxProps<Theme> | undefined = detailAction
      ? {
          cursor: 'pointer'
        }
      : undefined;

    return detailAction &&
      detailAction.contextAction &&
      detailAction.onClick ? (
      <TableClickableCell
        contextAction={detailAction.contextAction}
        onClick={detailAction.onClick}
        align={rowCell.align}
        key={rowCell.name}
      >
        {children}
      </TableClickableCell>
    ) : (
      <TableCell
        onClick={() => {
          if (detailAction && detailAction.onClick) {
            detailAction.onClick();
          }
        }}
        sx={rowProps}
        align={rowCell.align}
        key={rowCell.name}
      >
        {children}
      </TableCell>
    );
  };

  const TableContent = () => {
    return (
      <Table
        stickyHeader={scrollable}
        sx={{ minWidth: 750 }}
        aria-labelledby='tableTitle'
        size={'medium'}
      >
        <EnhancedTableHead
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleSortRequest}
          rowCount={rows.length}
          headCells={header}
          batchProcessing={batchProcessing ? batchProcessing : false}
        />
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={header.length} align='center'>
                {t(`common.table.loading`)}
              </TableCell>
            </TableRow>
          )}

          {!isLoading && !rows.length && (
            <NotFoundRow colSpan={header.length} />
          )}

          {!isLoading &&
            rows.map((row, index) => {
              const isItemSelected = isSelected(row.id.toString());
              const labelId = `enhanced-table-checkbox-${index}`;

              const detailAction: TableAction = row.cells
                .find((rowCell) => rowCell.type === TableRowCellType.ACTIONS)
                ?.value.find(
                  (action: TableAction) =>
                    action.type === TableActionType.DETAIL
                );

              return (
                <TableRow
                  hover
                  role='checkbox'
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id.toString()}
                  selected={isItemSelected}
                >
                  {row.cells
                    .filter(
                      (rowCell) => rowCell.type !== TableRowCellType.HIDDEN
                    )
                    .map((rowCell) => (
                      <RowCellElement
                        key={`rowCell${index}${rowCell.name}`}
                        rowCell={rowCell}
                        isItemSelected={isItemSelected}
                        labelId={labelId}
                        detailAction={detailAction}
                      />
                    ))}
                </TableRow>
              );
            })}

          {emptyRows > 0 && (
            <TableRow
              style={{
                height: 53 * emptyRows
              }}
            >
              <TableCell colSpan={header.length} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const MainCardTitle = title !== '' && (
    <Typography variant={'h5'}>{title}</Typography>
  );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    if (onPageChange) {
      onPageChange(event, newPage);
    }
  };

  const handleSearchChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onSearchChange && onSearchChange(e.target.value);
  };

  return (
    <MainCard
      content={false}
      title={
        typeof title === 'string' ? (
          MainCardTitle
        ) : (
          <Stack direction='row' spacing={2} alignItems='center'>
            {onSearchChange && (
              <OutlinedInput
                id='input-search-list-style1'
                placeholder={t(`${i18nComponentRoot}.searchInput`).toString()}
                size='small'
                value={search}
                //@ts-expect-error TODO: handle this custom property in TS
                highlightWhenValueNotEmpty={true}
                onChange={handleSearchChange}
                startAdornment={
                  <InputAdornment position='start'>
                    <IconSearch stroke={1.5} size='16px' />
                  </InputAdornment>
                }
                endAdornment={
                  search && (
                    <InputAdornment position='start'>
                      <IconX
                        style={{ cursor: 'pointer' }}
                        stroke={1.5}
                        size='16px'
                        onClick={() => {
                          onSearchChange('');
                        }}
                      />
                    </InputAdornment>
                  )
                }
              />
            )}
            {filter && filterChange && (
              <Badge color='secondary' badgeContent={touchedFiltersCount}>
                <Filter
                  categories={filter}
                  filterState={filterState}
                  onFilterSubmit={filterChange}
                />
              </Badge>
            )}
          </Stack>
        )
      }
      secondary={
        <Stack direction='row' spacing={2} alignItems='center'>
          {actions?.map((action, index) => {
            if (action.contextAction?.actionCode) {
              return (
                <ContextActionGuard
                  key={index}
                  module={appModule}
                  actionCode={action.contextAction?.actionCode}
                  resourceEntity={action.contextAction?.resourceEntity}
                  entityIdentificator={
                    action.contextAction?.entityIdentificator
                  }
                >
                  <Button
                    key={index}
                    variant='contained'
                    startIcon={action.icon}
                    onClick={action.onClick}
                    title={action.title}
                  >
                    {t(`${i18nComponentRoot}.createEntityButton`)}
                  </Button>
                </ContextActionGuard>
              );
            }
            return (
              <Button
                key={index}
                variant='contained'
                startIcon={action.icon}
                onClick={action.onClick}
                title={action.title}
              >
                {t(`${i18nComponentRoot}.createEntityButton`)}
              </Button>
            );
          })}
        </Stack>
      }
    >
      {/* table */}
      <TableContainer>
        {scrollable ? (
          <PerfectScrollbar
            style={{
              height: '100%',
              maxHeight: 'calc(80vh - 136px)',
              overflowX: 'hidden'
            }}
            component='div'
          >
            {TableContent()}
          </PerfectScrollbar>
        ) : (
          TableContent()
        )}
      </TableContainer>

      {/* table pagination */}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component='div'
          count={pagination?.totalElements ? pagination?.totalElements : 0}
          rowsPerPage={pagination?.size || localStorageRowsPerPage}
          page={pagination?.number}
          onPageChange={handlePageChange}
          onRowsPerPageChange={onChangePage}
          labelRowsPerPage={t(`common.table.rowsPerPage`)}
          labelDisplayedRows={labelDisplayedRows}
        />
      )}
    </MainCard>
  );
}
